import { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext.jsx';
import { useFontBinary } from '../../hooks/useFontBinary.js';

const OT_NAMES = {
  liga: 'Standard Ligatures', dlig: 'Discretionary Ligatures', hlig: 'Historical Ligatures',
  calt: 'Contextual Alternates', salt: 'Stylistic Alternates',
  ss01: 'Stylistic Set 1', ss02: 'Stylistic Set 2', ss03: 'Stylistic Set 3',
  ss04: 'Stylistic Set 4', ss05: 'Stylistic Set 5', ss06: 'Stylistic Set 6',
  ss07: 'Stylistic Set 7', ss08: 'Stylistic Set 8', ss09: 'Stylistic Set 9',
  ss10: 'Stylistic Set 10', ss11: 'Stylistic Set 11', ss12: 'Stylistic Set 12',
  smcp: 'Small Capitals', c2sc: 'Caps to Small Caps',
  lnum: 'Lining Figures', onum: 'Oldstyle Figures',
  pnum: 'Proportional Figures', tnum: 'Tabular Figures',
  frac: 'Fractions', ordn: 'Ordinals', zero: 'Slashed Zero',
  sups: 'Superscript', subs: 'Subscript',
  swsh: 'Swash', kern: 'Kerning', case: 'Case-Sensitive Forms',
  locl: 'Localized Forms', rlig: 'Required Ligatures',
  titl: 'Titling', ornm: 'Ornaments', cpsp: 'Capital Spacing',
};

export default function OpenTypeTab({ font: fontData, previewText }) {
  const { theme } = useTheme();
  const fileId = fontData.files?.[0]?.id;
  const { font: otFont, loading, error } = useFontBinary(fontData.id, fileId);
  const [features, setFeatures] = useState([]);
  const [enabled, setEnabled] = useState({});

  useEffect(() => {
    if (!otFont) return;
    const gsub = otFont.tables?.gsub;
    if (!gsub?.features) { setFeatures([]); return; }

    const featureTags = new Set();
    for (const f of gsub.features) {
      featureTags.add(f.tag);
    }
    setFeatures([...featureTags].sort());
  }, [otFont]);

  const toggle = (tag) => {
    setEnabled(prev => ({ ...prev, [tag]: !prev[tag] }));
  };

  const featureSettings = Object.entries(enabled)
    .filter(([, v]) => v)
    .map(([tag]) => `"${tag}" 1`)
    .join(', ');

  if (loading) return <div style={{ color: theme.textMuted, fontSize: 12, padding: 20 }}>Loading font data...</div>;
  if (error) return <div style={{ color: theme.red, fontSize: 12, padding: 20 }}>Error: {error}</div>;
  if (!otFont) return <div style={{ color: theme.textMuted, fontSize: 12, padding: 20 }}>No font file available</div>;
  if (features.length === 0) return <div style={{ color: theme.textMuted, fontSize: 12, padding: 20 }}>No OpenType features found</div>;

  return (
    <div>
      {/* Preview with applied features */}
      <div style={{
        border: `1px solid ${theme.border}`, padding: 16, marginBottom: 16,
      }}>
        <div style={{
          fontFamily: `'${fontData.family}', serif`,
          fontSize: 24, color: theme.text, lineHeight: 1.4,
          fontFeatureSettings: featureSettings || 'normal',
        }}>
          {previewText || 'AaBbCc 0123456789 fi fl ffi ffl Th ct st'}
        </div>
      </div>

      {/* Feature toggles */}
      <div style={{ fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: theme.textFaint, marginBottom: 8 }}>
        {features.length} opentype features available
      </div>
      {features.map(tag => (
        <div key={tag} style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 0', borderBottom: `1px solid ${theme.borderLight}`,
        }}>
          <div>
            <span style={{ fontSize: 12, color: theme.textSecondary }}>{OT_NAMES[tag] || tag}</span>
            <span style={{ fontSize: 10, color: theme.textGhost, marginLeft: 8 }}>{tag}</span>
          </div>
          <button
            onClick={() => toggle(tag)}
            title="toggle feature"
            aria-pressed={!!enabled[tag]}
            style={{ cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
          >
            <span style={{
              width: 30, height: 16, border: `1px solid ${theme.borderMedium}`, position: 'relative', display: 'block',
            }}>
              <span style={{
                position: 'absolute', top: 1, left: 1, width: 12, height: 12,
                background: enabled[tag] ? theme.accent : theme.textDisabled,
                transform: `translateX(${enabled[tag] ? '14px' : '0px'})`,
                transition: 'transform 200ms ease-in-out, background 200ms',
              }} />
            </span>
          </button>
        </div>
      ))}
    </div>
  );
}
